// FieldMappingRenderer - Key-value editor for field_mapping config

import React, { useState } from 'react';
import { Input } from '@/components/ui/Input';
import { useI18n } from '@/i18n';
import type { BaseCustomRendererProps } from './types';

export const FieldMappingRenderer: React.FC<BaseCustomRendererProps> = ({
  value,
  onChange,
  isDisabled = false,
}) => {
  const { t } = useI18n();
  const mapping = (value as Record<string, string>) || {};
  const [newArtifactField, setNewArtifactField] = useState('');
  const [newItemField, setNewItemField] = useState('');

  const handleUpdate = (updated: Record<string, string>) => {
    onChange(Object.keys(updated).length > 0 ? updated : undefined);
  };

  const handleChangeValue = (artifactField: string, itemField: string) => {
    handleUpdate({ ...mapping, [artifactField]: itemField });
  };

  const handleRemoveRow = (artifactField: string) => {
    const updated = { ...mapping };
    delete updated[artifactField];
    handleUpdate(updated);
  };

  const handleAddRow = () => {
    const trimmedArtifact = newArtifactField.trim();
    const trimmedItem = newItemField.trim();
    if (!trimmedArtifact || !trimmedItem) return;
    handleUpdate({ ...mapping, [trimmedArtifact]: trimmedItem });
    setNewArtifactField('');
    setNewItemField('');
  };

  const entries = Object.entries(mapping);

  return (
    <div className="space-y-2">
      {entries.length > 0 && (
        <div className="space-y-1.5">
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs font-medium text-gray-500 px-1">
            <span>{t('imageWorkflows.fieldMapping.artifactField')}</span>
            <span>{t('imageWorkflows.fieldMapping.itemField')}</span>
            <span />
          </div>
          {entries.map(([artifactField, itemField]) => (
            <div key={artifactField} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
              <Input
                value={artifactField}
                readOnly
                disabled={isDisabled}
                className="text-sm font-mono bg-gray-50"
              />
              <Input
                value={itemField}
                onChange={(e) => handleChangeValue(artifactField, e.target.value)}
                disabled={isDisabled}
                className="text-sm font-mono"
                placeholder={t('imageWorkflows.fieldMapping.itemFieldPlaceholder')}
              />
              <button
                type="button"
                onClick={() => handleRemoveRow(artifactField)}
                disabled={isDisabled}
                className="text-red-400 hover:text-red-600 disabled:opacity-40 text-sm px-1"
                aria-label="Remove"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center pt-1 border-t border-gray-100">
        <Input
          value={newArtifactField}
          onChange={(e) => setNewArtifactField(e.target.value)}
          disabled={isDisabled}
          className="text-sm font-mono"
          placeholder={t('imageWorkflows.fieldMapping.artifactFieldPlaceholder')}
        />
        <Input
          value={newItemField}
          onChange={(e) => setNewItemField(e.target.value)}
          disabled={isDisabled}
          className="text-sm font-mono"
          placeholder={t('imageWorkflows.fieldMapping.itemFieldPlaceholder')}
          onKeyDown={(e) => e.key === 'Enter' && handleAddRow()}
        />
        <button
          type="button"
          onClick={handleAddRow}
          disabled={isDisabled || !newArtifactField.trim() || !newItemField.trim()}
          className="text-blue-500 hover:text-blue-700 disabled:opacity-40 text-sm px-1"
          aria-label="Add"
        >
          +
        </button>
      </div>
      <p className="text-xs text-gray-400">
        {t('imageWorkflows.fieldMapping.hint')}
      </p>
    </div>
  );
};

export default FieldMappingRenderer;
